import { Box, styled } from "@mui/material";

import {
    COLORS,
    MAIN_MAX_WIDTH,
    RADIUS,
    SHADOW,
    TYPOGRAPHY,
} from "./SignalWizard.constants";

/** Keeps `$`-prefixed styling props from reaching MUI components / the DOM. */
export const transientOptions = {
    shouldForwardProp: (prop: PropertyKey) => !String(prop).startsWith("$"),
};

export const Shell = styled(Box)({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "100dvh",
    backgroundColor: COLORS.background,
    color: COLORS.onSurface,
    WebkitFontSmoothing: "antialiased",
    ...TYPOGRAPHY.bodyMd,
    "& *": {
        fontFamily: "inherit",
    },
});

export const Main = styled("main")(({ theme }) => ({
    flex: 1,
    width: "100%",
    maxWidth: MAIN_MAX_WIDTH,
    margin: "0 auto",
    padding: "32px 16px 48px",
    [theme.breakpoints.up("md")]: {
        padding: "32px 24px 48px",
    },
}));

export const PageHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 32,
    [theme.breakpoints.up("md")]: {
        flexDirection: "row",
        alignItems: "flex-end",
    },
}));

export const Overline = styled("span")({
    ...TYPOGRAPHY.overline,
    color: COLORS.onSurfaceVariant,
});

export const PageTitle = styled("h1")(({ theme }) => ({
    margin: 0,
    color: COLORS.primary,
    ...TYPOGRAPHY.headlineLgMobile,
    [theme.breakpoints.up("md")]: {
        ...TYPOGRAPHY.headlineLg,
    },
}));

export const PageDescription = styled("p")({
    margin: "4px 0 0",
    color: COLORS.onSurfaceVariant,
    ...TYPOGRAPHY.bodyMd,
});

export const SectionHeader = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 4px",
    marginBottom: 10,
});

export const SectionTitle = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: 8,
    ...TYPOGRAPHY.overline,
    color: COLORS.onSurfaceVariant,
});

export const StatusDot = styled(
    "span",
    transientOptions,
)<{ $color?: string }>(({ $color }) => ({
    width: 6,
    height: 6,
    borderRadius: RADIUS.full,
    backgroundColor: $color ?? COLORS.secondary,
    flexShrink: 0,
}));

export const CountPill = styled("span")({
    ...TYPOGRAPHY.labelSm,
    color: COLORS.onSurfaceVariant,
    backgroundColor: "rgba(232, 232, 234, 0.6)",
    padding: "2px 8px",
    borderRadius: RADIUS.full,
});

export const Card = styled(Box)({
    backgroundColor: COLORS.surfaceLowest,
    borderRadius: RADIUS.card,
    border: `1px solid ${COLORS.border}`,
    boxShadow: SHADOW.sm,
    overflow: "hidden",
});

/** Shared `sx` for the wizard's `StandartButton`s. */
export const wizardButtonSx = {
    textTransform: "none",
    gap: "2px",
    "&.Mui-disabled": { opacity: 0.4 },
    "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg": { fontSize: 16 },
};
